// A single "I tapped a vote before signing in" intent, stashed across the auth
// gate so we can replay it once the user authenticates ("lock it in").
//
// Stored in sessionStorage, not localStorage, on purpose: the intent belongs to
// the one tab that started it and must not be visible to other tabs. The login
// flow reloads the page within the same tab and signup stays on it, so
// sessionStorage survives the whole journey — but a second tab the user happens
// to have open can't consume the same intent and toggle the vote back off.
// Every access is guarded — blocked/absent storage just means there's no pending
// vote, which is harmless (voting still works normally once signed in).
const KEY = 'pinion_pending_vote';

// Ignore a stash older than this, so a vote the user abandoned an hour ago
// doesn't surprise them by landing when they later sign in for another reason.
const MAX_AGE_MS = 60 * 60 * 1000;

interface StoredPendingVote {
  questionId: string;
  answer: boolean;
  ts: number;
}

export interface PendingVote {
  questionId: string;
  answer: boolean;
}

export const setPendingVote = (vote: PendingVote): void => {
  try {
    const stored: StoredPendingVote = { ...vote, ts: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // No storage — the vote-first lock just won't happen; voting still works
    // normally once the user is signed in.
  }
};

// Read the pending vote WITHOUT consuming it. Returns null when there's nothing
// valid to replay. The caller clears it with clearPendingVote() only once the
// vote has actually been resolved, so a failed replay can be retried on the next
// load instead of being silently lost.
export const peekPendingVote = (): PendingVote | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredPendingVote;
    if (
      !parsed ||
      typeof parsed.questionId !== 'string' ||
      typeof parsed.answer !== 'boolean'
    ) {
      clearPendingVote(); // corrupt entry — don't let it linger
      return null;
    }
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > MAX_AGE_MS) {
      clearPendingVote(); // stale — drop it
      return null;
    }
    return { questionId: parsed.questionId, answer: parsed.answer };
  } catch {
    clearPendingVote();
    return null;
  }
};

export const clearPendingVote = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
};

// Peek-and-clear in one step, for callers that resolve the vote immediately and
// unconditionally (e.g. signup, where a brand-new account can have no prior vote
// to toggle off, so a plain replay is always a safe create).
export const takePendingVote = (): PendingVote | null => {
  const pending = peekPendingVote();
  if (pending) clearPendingVote();
  return pending;
};
